import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FiUsers, FiCalendar, FiGift, FiUser } from 'react-icons/fi';
import { HUB_GUTTER_CSS } from './hubType';
import { PAGE_GUTTER_DESKTOP } from '../lib/pageGutter';
import { type HubNavigation, normalizeHubPath, scrollHubTabToTop } from './hubNavigation';

/* THE-859 (board override of the canvas): no divider under the desktop nav —
   Kate's laptop review flagged the line between it and the "Gift guides +
   search" row below. */
// THE-1161 shepherd QA: this was `padding: 0 4%`, a viewport-percentage inset
// that never matched the page's own gutter (4% lands at 51/58/69px at
// 1280/1440/1728 against the title's 64/64/144) — the wordmark drifted left
// of everything under it, worse the wider the screen got. PAGE_GUTTER_DESKTOP
// is what every other piece of the hub now reads for this same alignment.
// `$wideGutter` (giftGuideHubFlag.ts): off/omitted reproduces the pre-PR
// flat `4%` until Kate reviews this at a preview link.
// THE-1263: this bar's own fixed rendered height — BoardLayout needs it (as
// a plain number, not a CSS length) to work out where ITS OWN wash picks up
// so the two paint one continuous fade rather than two independently-tinted
// boxes. Keep this in sync with `min-height` below; nothing else reads it.
export const HUB_DESKTOP_NAV_HEIGHT_PX = 76;
const Desktop = styled.header<{ $embedded?: boolean; $wideGutter?: boolean; $boardWash?: string | null; $pinned?: boolean; $raised?: boolean }>`
  display: none;
  @media (min-width: 1024px) {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    min-height: ${HUB_DESKTOP_NAV_HEIGHT_PX}px;
    padding: 0 ${({ $wideGutter }) => ($wideGutter ? PAGE_GUTTER_DESKTOP : '4%')};
    /* THE-1263: $boardWash carries whatever background CSS BoardLayout
       computed for this exact state (see HubBoardWashContext's own doc
       comment) — a flat color before BoardLayout has measured its own
       wash wrapper, or (Kate 2026-10-27: "one fade ... straight through
       the hub top bar," not a flat band) a gradient that hands off to
       BoardLayout's own wash at the measured boundary once it has. This
       bar is a separate DOM box ahead of the board's layout, not nested
       inside its wash wrapper the way the non-hub nav row is, so it can't
       just go transparent — there's nothing painted behind it to show
       through. */
    background: ${({ $embedded, $boardWash }) => ($embedded ? 'transparent' : $boardWash ?? 'white')};
    /* The board scrolls the document with its card and tabs pinned directly
       under this bar (BoardLayout's \`Page\`), so this has to hold too. Other
       hub pages let the bar scroll away as before. */
    ${({ $pinned }) => $pinned && 'position: sticky; top: 0; z-index: 24;'}
    /* THE-1391: the People page (\`/\`) needs this bar ABOVE ComingUpStrip's
       reclaimed confetti headroom for hit-testing, but \`/\` must keep
       scrolling the bar away with the rest of the document exactly as it
       does today — it is the first-time marketing homepage, and Kate's call
       was clicks-only, no scroll-behaviour change. \`position: sticky\`
       (above) would pin it to the viewport, so this path stays in normal
       flow (no \`top\`) and only opens a stacking context via
       \`position: relative\` + \`z-index\`, the same z-index \`$pinned\` uses,
       so this bar paints (and hit-tests) above ComingUpStrip's Scroller
       without changing where it sits as the page scrolls. */
    ${({ $raised }) => $raised && 'position: relative; z-index: 24;'}
  }
`;
/* THE-1265 — logo 28px, the same size as the board's own nav row
   (SiteNavLinks' `SiteWordmark`), reading `theme.font.serif` instead of a
   literal Georgia stack so the two wordmarks stay byte-identical in font too. */
const Wordmark = styled(Link)`
  color: ${({ theme }) => theme.color.clay};
  font-family: ${({ theme }) => theme.font.serif};
  font-style: italic;
  letter-spacing: 0.025em;
  font-size: 28px;
  text-decoration: none;
`;
const DesktopTabs = styled.nav`display: flex; gap: 32px;`;
const Account = styled.div`justify-self: end;`;
/* THE-1265 — "Log in" onto the board's one pill spec (`theme.pill`, THE-1185):
   40px tall, 16px side padding, 14px/600 ink text, white fill, a 1px
   `warmBorder` border — the same shape `BoardUpdatePicksPill`'s `Pill` reads.
   Was a plain text link (border: 0; background: none); that drifted off the
   nav-row standard board-wide once THE-1185 settled it. */
export const HubLoginButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: ${({ theme }) => theme.pill.height};
  padding: 0 ${({ theme }) => theme.pill.paddingX};
  border: ${({ theme }) => theme.pill.border} solid ${({ theme }) => theme.color.warmBorder};
  border-radius: ${({ theme }) => theme.pill.radius};
  background: #ffffff;
  color: ${({ theme }) => theme.color.foreground};
  font-family: ${({ theme }) => theme.font.sans};
  font-size: ${({ theme }) => theme.pill.text};
  font-weight: ${({ theme }) => theme.weight.semibold};
  white-space: nowrap;
  cursor: pointer;
`;
/**
 * The PINNED mobile top bar — wordmark left, `Log in` right (PRD "People tab":
 * "A header with the Thea logo and 'Log in' that stays pinned while scrolling",
 * replacing today's header, which says "Sign in" and scrolls away).
 *
 * TWO ROOTS ONLY, and the canvas is the reason the rule is not "every tab":
 * p01 (mobile home) draws wordmark + `Log in`; p04 (mobile You, signed out)
 * draws the wordmark ALONE, because that page's own rust
 * `Log in or create account` card is the door; p02 (Calendar) and p03 (the hub)
 * draw no top bar at all — their first row is the month heading / the
 * `Gift guides` title. So this is chrome the two marketing-shaped roots carry,
 * not frame chrome, and `HubNavigationView` renders it only for them.
 *
 * KEYED ON THE ROOT PATH, NOT `navigation.active`. `resolveHubNavigation`
 * resolves `active: 'people'` for every path it does not recognise — boards
 * (`/board`, `/b/:id`) and `/quiz/results/*` all land there — and those pages
 * draw their own headers. Matching `/` exactly keeps this off them.
 *
 * `sticky`, not `fixed`: it sits in the document flow ahead of the page, so it
 * holds the top of the viewport without the content needing to reserve space
 * for it, and without joining the fixed bottom bar's stacking contest.
 */
const MobileTop = styled.header`
  position: sticky; top: 0; z-index: 24;
  display: flex; align-items: center; justify-content: space-between; gap: 16px;
  padding: 14px 20px; background: white; border-bottom: 1px solid #f1ece7;
  @media (min-width: 640px) { padding: 14px 32px; }
  @media (min-width: 1024px) { display: none; }
`;
const Mobile = styled.nav`
  position: fixed; bottom: var(--hub-bottom-inset, 0px); left: 0; right: 0;
  z-index: 25; display: grid; grid-template-columns: repeat(4, 1fr);
  padding: 8px 0 4px; background: white; border-top: 1px solid #e5dfd8;
  @media (display-mode: standalone) { padding-bottom: max(8px, env(safe-area-inset-bottom)); }
  @media (min-width: 1024px) { display: none; }
`;
/* THE-1265 — the desktop nav row onto THE-1185's standard: `theme.text.tabs`
   (17px desktop) / `theme.weight` for size+weight, ink `foreground` selected
   vs. grey `mutedForeground` unselected with a 2px ink underline, the same
   recipe `SiteNavLinks`' `NavLink` reads for the board's own nav row. The
   mobile bottom tab bar below 1024px is THE-1232's territory, untouched here
   (still its own 11px/clay scale). */
const Tab = styled(Link)<{ $active: boolean }>`
  display: flex; flex-direction: column; align-items: center; gap: 4px;
  padding: 6px 4px; text-decoration: none; font-size: 11px;
  font-weight: ${({ $active }) => $active ? 700 : 500};
  color: ${({ $active, theme }) => $active ? theme.color.clay : '#68625d'};
  svg { width: 22px; height: 22px; }
  @media (min-width: 1024px) {
    font-size: ${({ theme }) => theme.text.tabs.desktop};
    font-weight: ${({ $active, theme }) => $active ? theme.weight.semibold : theme.weight.regular};
    color: ${({ $active, theme }) => $active ? theme.color.foreground : theme.color.mutedForeground};
    padding-top: 12px; padding-bottom: 6px;
    border-bottom: 2px solid ${({ $active, theme }) => $active ? theme.color.foreground : 'transparent'};
    svg { display: none; }
  }
`;
/**
 * The You tab's icon slot, made a positioning context for the dot. A wrapper
 * rather than `position: relative` on the `<Tab>` itself: the dot is pinned to
 * the ICON's corner, and the Tab box is icon + label, so anchoring to the Tab
 * would float the dot beside the word on one breakpoint and the glyph on the
 * other.
 */
const TabIcon = styled.span`
  position: relative;
  display: inline-flex;
  line-height: 0;
`;
/**
 * The no-number notification dot (THE-973 / THE-967 PR 3). ONE position in the
 * tab bar carries BOTH dots — the guest's "you have saves but no account" and
 * the signed-in "you have notifications" — because they are mutually exclusive
 * by auth state (`useHubYouTabDot`), so the user never has to learn two places.
 *
 * `#d93025` is the same red as every other red dot on the site
 * (`AccountAlerts`' inline nudge dot, `HeaderAccountMenu`'s avatar threshold
 * dot) — a new notification colour would read as a different kind of thing.
 *
 * NO NUMBER, deliberately, and the two reasons are different per dot: the guest
 * dot has nothing to count, and the signed-in dot's count already has a home on
 * desktop (the avatar number, PR 1). The cream ring is the one non-obvious bit
 * — it keeps the dot legible where it overlaps the dark icon stroke.
 *
 * `data-hub-you-dot` carries WHICH dot this is, so a test and a screenshot can
 * both tell the guest state from the signed-in one at the same pixel.
 */
const TabDot = styled.span`
  position: absolute;
  top: -2px;
  right: -4px;
  width: 8px;
  height: 8px;
  border-radius: 9999px;
  background: #d93025;
  box-shadow: 0 0 0 2px white;
`;
const Trail = styled.nav<{ $rootOnly: boolean; $embedded?: boolean; $wideGutter?: boolean }>`
  ${HUB_GUTTER_CSS}
  display: flex; align-items: center; gap: 10px; padding-top: 20px; padding-bottom: 20px;
  background: ${({ $embedded }) => $embedded ? 'transparent' : 'white'}; color: ${({ theme }) => theme.color.clay};
  font-size: 15px;
  a { color: inherit; font-weight: 600; text-decoration: none; }
  .hub-crumb { display: inline-flex; align-items: center; gap: 10px; }
  .desktop-chevron { display: none; }
  @media (min-width: 1024px) {
    display: ${({ $rootOnly }) => $rootOnly ? 'none' : 'flex'};
    .hub-root-crumb, .mobile-chevron { display: none; }
    .desktop-chevron { display: inline; }
  }
`;
const tabs = [
  { id: 'people', label: 'People', href: '/', Icon: FiUsers },
  { id: 'calendar', label: 'Calendar', href: '/calendar', Icon: FiCalendar },
  { id: 'guides', label: 'Gift guides', href: '/gift-guides', Icon: FiGift },
  { id: 'you', label: 'You', href: '/you', Icon: FiUser },
] as const;

export function HubNavigationView({ navigation, pathname, hrefFor, account, mobileAccount, mobileRef, embedded = false, youDot = null, wideGutterEnabled = false, hideMobileTabBar = false, boardWash = null, pinDesktopBar, raiseDesktopBar }: {
  navigation: HubNavigation; pathname: string; hrefFor: (path: string) => string;
  account: React.ReactNode;
  /**
   * THE-1161 (rides the Gift Guide hub gate; no preview switch of its own) — defaults to `false` so
   * every existing story/test keeps rendering today's production layout
   * when the hub gate is off. `HubNavigationFrame.tsx` resolves the flag once and
   * threads it down; this view stays Firebase/Router-free, matching every
   * other gate in the hub.
   */
  wideGutterEnabled?: boolean;
  /**
   * Which no-number dot the You tab carries, or `null` for none — `'guest'`
   * (saves but no account yet) or `'notifications'` (>= 1 notification row).
   * Resolved by `useHubYouTabDot` in the frame, passed in as data so this view
   * stays Firebase-free and its stories can draw both states.
   *
   * MOBILE-ONLY BY CONSTRUCTION, not by a media query: `you` is the one tab the
   * desktop nav below filters out, so this prop can only ever reach the fixed
   * bottom bar. Desktop keeps `Log in` in the header for the guest and
   * `HubAccountMenu`'s number-on-avatar for the account holder — the same row
   * model (`hubAlerts.ts`) this dot reads, so the two cannot disagree.
   */
  youDot?: 'guest' | 'notifications' | null;
  /** Right slot of the pinned mobile top bar. A SEPARATE node from `account`,
   *  not the same one rendered twice: the signed-in desktop slot is
   *  `HubAccountMenu`, a stateful dropdown, and mounting a second copy to be
   *  hidden by a media query would give the page two of them. Mobile's
   *  signed-in account lives on the You tab, so the frame passes `null` there
   *  and this bar is wordmark-only — which is also what canvas p04 draws. */
  mobileAccount?: React.ReactNode;
  mobileRef?: React.Ref<HTMLElement>;
  embedded?: boolean;
  /** THE-1187: the per-person Saved page's own phone-width case — the fixed
   *  bottom tab bar is absent there (the floating "Share list" takes its
   *  place), so its one caller (`HubNavigationFrame`) passes this true and
   *  must also arrange a sign-in fallback, since the hidden bar's "You" tab
   *  was that surface's only door inside this frame. */
  hideMobileTabBar?: boolean;
  /** THE-1263: the background BoardLayout wants this bar to paint while its
   *  header card is at rest on desktop, or `null` otherwise — see
   *  `Desktop`'s own doc comment. */
  boardWash?: string | null;
  /** Hold the desktop bar at the top of the viewport (board routes). */
  pinDesktopBar?: boolean;
  /** THE-1391: open a stacking context above the People page's ComingUpStrip
   *  without pinning the bar to the viewport — see `Desktop`'s own doc
   *  comment. Mutually exclusive with `pinDesktopBar` in practice: the frame
   *  only ever sets one or the other. */
  raiseDesktopBar?: boolean;
}) {
  const root = normalizeHubPath(pathname);
  const topBar = root === '/' ? 'people' : root === '/you' ? 'you' : null;
  const tab = ({ id, label, href, Icon }: typeof tabs[number]) => (
    <Tab key={id} to={hrefFor(href)} $active={navigation.active === id}
      aria-current={navigation.active === id ? 'page' : undefined}
      onClick={event => {
        // A child page's highlighted tab still goes to its root. Only a true
        // re-tap at the root scrolls locally, without a history entry.
        if (navigation.active === id && normalizeHubPath(pathname) === href && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
          event.preventDefault(); scrollHubTabToTop();
        }
      }}>
      <TabIcon>
        <Icon aria-hidden="true" />
        {id === 'you' && youDot
          ? <TabDot data-testid="hub-you-tab-dot" data-hub-you-dot={youDot} aria-hidden="true" />
          : null}
      </TabIcon><span>{label}</span>
    </Tab>
  );
  return <>
    <Desktop $embedded={embedded} $wideGutter={wideGutterEnabled} $boardWash={boardWash} $pinned={pinDesktopBar} $raised={raiseDesktopBar} data-testid="hub-desktop-navigation">
      <Wordmark to={hrefFor('/')}>thea</Wordmark>
      <DesktopTabs aria-label="Primary navigation">{tabs.filter(t => t.id !== 'you').map(tab)}</DesktopTabs>
      <Account>{account}</Account>
    </Desktop>
    {topBar && <MobileTop data-testid="hub-mobile-header" data-hub-top-bar={topBar}>
      <Wordmark to={hrefFor('/')}>thea</Wordmark>
      {topBar === 'people' ? mobileAccount : null}
    </MobileTop>}
    {navigation.crumbs.length > 0 && <Trail $embedded={embedded} $wideGutter={wideGutterEnabled} aria-label="Breadcrumb" $rootOnly={navigation.crumbs.length === 1 && navigation.crumbs[0].href === '/gift-guides'}>
      {navigation.crumbs.map((crumb, i) => <span key={crumb.href} className={`hub-crumb${crumb.href === '/gift-guides' ? ' hub-root-crumb' : ''}`}>
        <span className="mobile-chevron" aria-hidden="true">{i === 0 ? '‹' : '›'}</span>
        <span className="desktop-chevron" aria-hidden="true">‹</span>
        <Link to={hrefFor(crumb.href)} state={crumb.state}>{crumb.label}</Link>
      </span>)}
    </Trail>}
    {!hideMobileTabBar && <Mobile aria-label="Primary navigation" ref={mobileRef}>{tabs.map(tab)}</Mobile>}
  </>;
}
